/**
 * Which OAuth providers are actually switched on in Supabase (Authentication → Providers).
 *
 * The sign-in window shows ONLY these, so a player never taps a button that answers
 * "provider is not enabled". Enabling one in the Supabase dashboard makes its button appear;
 * nothing in the app changes. Read from Supabase's public `/auth/v1/settings` (anon key).
 * While the answer is loading, or if it cannot be read, no OAuth button is shown — email
 * sign-in is always there.
 */
import { useQuery } from '@tanstack/react-query';
import type { OAuthProvider } from './useAccountId';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** The settings list changes only when someone edits the dashboard. */
const PROVIDER_SETTINGS_STALE_MS = 10 * 60 * 1000;

/** Our provider id → the key(s) Supabase reports it under (X is still `twitter` there). */
const SETTINGS_KEYS: Record<OAuthProvider, readonly string[]> = {
  google: ['google'],
  discord: ['discord'],
  github: ['github'],
  apple: ['apple'],
  x: ['x', 'twitter'],
  facebook: ['facebook'],
};

async function fetchEnabledProviders(): Promise<ReadonlySet<OAuthProvider>> {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/settings`, {
    headers: { apikey: SUPABASE_ANON_KEY ?? '' },
  });
  if (!response.ok) return new Set();
  const settings = (await response.json()) as { external?: Record<string, boolean> };
  const external = settings.external ?? {};
  const enabled = (Object.keys(SETTINGS_KEYS) as OAuthProvider[]).filter((provider) =>
    SETTINGS_KEYS[provider].some((key) => external[key] === true),
  );
  return new Set(enabled);
}

export function useEnabledOAuthProviders(): ReadonlySet<OAuthProvider> {
  const { data } = useQuery({
    queryKey: ['supabaseEnabledOAuthProviders'],
    queryFn: fetchEnabledProviders,
    enabled: Boolean(SUPABASE_URL && SUPABASE_ANON_KEY),
    staleTime: PROVIDER_SETTINGS_STALE_MS,
  });
  return data ?? new Set();
}
